import type { ZodTypeAny } from "zod";
import { Field, Segmented, Select, Slider, Stepper, TextInput, Toggle } from "../ui";
import { introspectSettings, type SettingsField } from "./introspect";

// Generischer Settings-Renderer aus dem Zod-Schema (Fallback-Engine, §3.4-Prinzip:
// Schema = SSOT). Tools mit eigener SettingsUI überschreiben die Präsentation; dieser
// Renderer ist der „kind-basierte Fallback" fürs Formular — analog zum Result-Fallback
// (D32/D43). Demonstriert: ein neues Tool ist ohne handgebaute UI sofort bedienbar.

export function FieldControl({
  field,
  value,
  onChange,
}: {
  field: SettingsField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (field.kind) {
    case "boolean":
      return <Toggle checked={Boolean(value)} onChange={onChange} />;
    case "enum":
      return field.options && field.options.length <= 5 ? (
        <Segmented
          value={String(value ?? field.options[0])}
          options={field.options.map((o) => ({ value: o, label: o }))}
          onChange={onChange}
        />
      ) : (
        <Select value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
    case "number": {
      const n = typeof value === "number" ? value : (field.min ?? 0);
      const bounded = field.min !== undefined && field.max !== undefined && field.max - field.min <= 100;
      return bounded && !field.int ? (
        // Slider trägt kein Suffix → die Einheit (D70) daneben anzeigen, damit sie nicht verloren geht.
        <div className="flex items-center gap-2">
          <Slider value={n} min={field.min} max={field.max} step={field.step} onChange={onChange} />
          {field.unit && <span className="shrink-0 text-xs text-muted">{field.unit}</span>}
        </div>
      ) : (
        <Stepper value={n} min={field.min} max={field.max} step={field.step} suffix={field.unit} onChange={onChange} />
      );
    }
    default:
      return (
        <TextInput
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.optional ? "optional" : ""}
        />
      );
  }
}

export function SchemaForm({
  schema,
  value,
  onChange,
  hidden,
}: {
  schema: ZodTypeAny;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** D101: modell-versteckte Felder + 'model' selbst (rendert der ModelPicker) — rein UI;
   *  der Server strippt versteckte Werte ohnehin (withModelGating). */
  hidden?: ReadonlySet<string>;
}) {
  const fields = introspectSettings(schema).filter((f) => !hidden?.has(f.key));
  return (
    <div className="space-y-4">
      {fields.map((f) => (
        <Field key={f.key} label={f.key}>
          <FieldControl field={f} value={value[f.key]} onChange={(v) => onChange({ ...value, [f.key]: v })} />
        </Field>
      ))}
    </div>
  );
}
